import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';

import {
  formatDate,
  formatCurrency,
  calcInvoiceTotal,
} from '#common/utils';
import {
  INOVICE_ITEM_TYPES,
} from '#common/constants';
import EnhancedTable, { EnhancedTableData, EnhancedTableHeader } from '#components/EnhancedTable';

interface InvoiceItem {
  type: string;
  value: string;
}

interface InvoiceRow {
  number?: string;
  items?: InvoiceItem[];
  invoiceDate?: string | number | Date;
  total?: number;
  [key: string]: unknown;
}

interface UserInvoicesProps {
  order: {
    additionalCharges?: Array<{ _id: unknown; name?: string; [key: string]: unknown }>;
    [key: string]: unknown;
  };
  invoices: EnhancedTableData;
}

export default function UserInvoices(props: UserInvoicesProps) {
  const {
    order,
    invoices,
  } = props;
  const { t } = useTranslation('order');

  const { additionalCharges } = order;

  function createDownloadInvoiceHandler(invoice: InvoiceRow) {
    return () => {
      const url = `/api/downloadInvoice?invoiceNumber=${invoice.number}`;
      window.open(url);
    };
  }

  const invoicesHeaders: EnhancedTableHeader[] = [{
    id: 'id',
    label: t('invoicesTable.invoiceNumber'),
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.number || '-'),
  }, {
    id: 'items',
    label: t('invoicesTable.items'),
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.items || [])
      .map(({ type, value }) => {
        let text = '';
        if (type === INOVICE_ITEM_TYPES.BARCODE_ITEM) {
          text = value;
        } else if (type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE) {
          const find = additionalCharges?.find((ac) => ac._id === value);
          text = find?.name ?? '';
        }

        return (<div key={value}>{text}</div>);
      }),
  }, {
    id: 'qty',
    label: t('invoicesTable.qty'),
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.items || [])
      .length,
  }, {
    id: 'invoiceDate',
    label: t('invoicesTable.createdDate'),
    editable: false,
    renderer: (invoice: InvoiceRow) => formatDate(invoice.invoiceDate as string | number | Date),
  }, {
    id: 'total',
    label: t('invoicesTable.total'),
    editable: false,
    renderer: (invoice: InvoiceRow) => {
      const total = invoice.total
        || calcInvoiceTotal(
          invoice as unknown as Parameters<typeof calcInvoiceTotal>[0],
          order as unknown as Parameters<typeof calcInvoiceTotal>[1],
          additionalCharges as unknown as Parameters<typeof calcInvoiceTotal>[2],
        );
      return formatCurrency(total);
    },
  }, {
    id: 'actions',
    label: t('invoicesTable.action'),
    hideForPrint: true,
    renderer: (o: InvoiceRow) => {
      if (!o) {
        return null;
      }

      return (
        <Link
          key="btn-edit"
          component="button"
          onClick={createDownloadInvoiceHandler(o)}
        >
          {t('invoicesTable.download')}
        </Link>
      );
    },
  }];

  return (
    <Box>
      <EnhancedTable
        name="invoices"
        headers={invoicesHeaders}
        {...invoices}
      />
    </Box>
  );
}
